<!--
  Pick a stretch of time: a row of presets (this month, last month, 90 days, this year, all time)
  and Custom, which opens one slider with a handle at each end; drag the middle to slide the whole
  window. The slider spans the data you have (first to last). Remembered per page on this device.
-->
<script lang="ts">
  import { presetRange, type DateRange, type RangePreset } from "@split/core";
  import { app } from "../lib/store.svelte.ts";

  let {
    key,
    range = $bindable(),
    min,
    max,
  }: {
    key: string;
    range?: DateRange;
    /** The first and last dates with data: the slider's ends. */
    min?: string | null;
    max?: string | null;
  } = $props();

  const DAY = 86_400_000;
  // Whole days since 1970 (noon keeps it clear of time zones; floor so a date maps to itself).
  const toDay = (iso: string) => Math.floor(Date.parse(`${iso}T12:00:00Z`) / DAY);
  const toIso = (n: number) => new Date(n * DAY).toISOString().slice(0, 10);

  function read(): { preset: RangePreset; from: string; to: string } {
    try {
      const v = JSON.parse(localStorage.getItem(`range:${key}`) ?? "null");
      if (v && typeof v.preset === "string") return v;
    } catch {
      /* fine */
    }
    return { preset: "all", from: "", to: "" };
  }
  const start = read();
  let preset = $state<RangePreset>(start.preset);
  let from = $state(start.from);
  let to = $state(start.to);

  const hi = $derived(toDay(max && max < app.today ? max : app.today));
  const lo = $derived(Math.min(hi - 1, toDay(min ?? toIso(hi - 365))));
  const a = $derived(from ? Math.max(lo, Math.min(hi, toDay(from))) : lo);
  const b = $derived(to ? Math.max(lo, Math.min(hi, toDay(to))) : hi);
  const pct = (n: number) => ((n - lo) / Math.max(1, hi - lo)) * 100;

  function startCustom() {
    if (preset !== "custom") {
      // Start from whatever was showing, so switching to Custom doesn't jump.
      const r = presetRange(preset, app.today);
      from = r.from ?? toIso(lo);
      to = r.to ?? app.today;
    }
    preset = "custom";
  }
  function setA(v: number) {
    from = toIso(Math.min(v, b));
  }
  function setB(v: number) {
    to = toIso(Math.max(v, a));
  }

  // Drag the filled middle to slide the window, keeping its length.
  let width = $state(1);
  let drag: { x: number; a: number; b: number } | null = null;
  function grab(e: PointerEvent) {
    drag = { x: e.clientX, a, b };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }
  function move(e: PointerEvent) {
    if (!drag) return;
    const span = drag.b - drag.a;
    const d = Math.round(((e.clientX - drag.x) / Math.max(1, width)) * (hi - lo));
    const na = Math.max(lo, Math.min(hi - span, drag.a + d));
    from = toIso(na);
    to = toIso(na + span);
  }
  function drop() {
    drag = null;
  }
  function nudge(e: KeyboardEvent) {
    const step = e.shiftKey ? 30 : 7;
    const d = e.key === "ArrowLeft" ? -step : e.key === "ArrowRight" ? step : 0;
    if (!d) return;
    e.preventDefault();
    const span = b - a;
    const na = Math.max(lo, Math.min(hi - span, a + d));
    from = toIso(na);
    to = toIso(na + span);
  }

  $effect(() => {
    range = presetRange(preset, app.today, { from: from || null, to: to || null });
    try {
      localStorage.setItem(`range:${key}`, JSON.stringify({ preset, from, to }));
    } catch {
      /* fine */
    }
  });

  const label = (n: number) =>
    new Date(n * DAY).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
  const PRESETS: [RangePreset, string][] = [
    ["month", "This month"],
    ["last-month", "Last month"],
    ["90d", "90 days"],
    ["year", "This year"],
    ["all", "All time"],
  ];
</script>

<div class="range">
  <div class="presets" role="group" aria-label="Time range">
    {#each PRESETS as [v, text] (v)}
      <button type="button" aria-pressed={preset === v} onclick={() => (preset = v)}>{text}</button>
    {/each}
    <button type="button" aria-pressed={preset === "custom"} onclick={startCustom}>Custom</button>
  </div>
  {#if preset === "custom"}
    <div class="slider" style:--a="{pct(a)}%" style:--b="{pct(b)}%" bind:clientWidth={width}>
      <div class="track"></div>
      <div
        class="fill"
        role="slider"
        tabindex="0"
        aria-label="Move the range"
        aria-valuemin={lo}
        aria-valuemax={hi}
        aria-valuenow={a}
        aria-valuetext="{label(a)} to {label(b)}"
        onpointerdown={grab}
        onpointermove={move}
        onpointerup={drop}
        onpointercancel={drop}
        onkeydown={nudge}
      ></div>
      <!-- Re-made when the ends change, so the browser never clamps a value to old ends. -->
      {#key `${lo}:${hi}`}
      <input type="range" min={lo} max={hi} step="1" value={a} oninput={(e) => setA(+e.currentTarget.value)} aria-label="From" aria-valuetext={label(a)} />
      <input type="range" min={lo} max={hi} step="1" value={b} oninput={(e) => setB(+e.currentTarget.value)} aria-label="To" aria-valuetext={label(b)} />
      {/key}
    </div>
    <div class="ends">
      <span>{label(a)}</span>
      <span class="muted">{b - a + 1} days</span>
      <span>{label(b)}</span>
    </div>
  {/if}
</div>

<style>
  .range {
    display: grid;
    gap: 8px;
    min-width: 0;
    max-width: 100%;
  }

  .presets {
    display: flex;
    gap: 2px;
    padding: 3px;
    border-radius: 999px;
    background: var(--wash);
    overflow-x: auto;
    scrollbar-width: none;
    max-width: 100%;
  }

  /* Phones: two neat rows of three instead of a strip that scrolls. */
  @media (max-width: 560px) {
    .presets {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      border-radius: 14px;
    }

    .presets button {
      border-radius: 11px;
      padding: 4px 6px;
    }
  }

  .presets::-webkit-scrollbar {
    display: none;
  }

  .presets button {
    flex: none;
    min-height: 30px;
    padding: 4px 12px;
    border: 0;
    border-radius: 999px;
    background: transparent;
    color: var(--ink-2);
    font: inherit;
    font-size: var(--t-sm);
    font-weight: 600;
    white-space: nowrap;
    cursor: pointer;
  }

  .presets button[aria-pressed="true"] {
    background: var(--surface);
    color: var(--ink);
    box-shadow: 0 1px 2px rgb(0 0 0 / 0.12);
  }

  .slider {
    position: relative;
    height: 28px;
    margin: 0 10px;
  }

  .track {
    position: absolute;
    inset: 12px 0;
    height: 4px;
    border-radius: 999px;
    background: var(--rule);
  }

  /* The filled part: a wide grab area with a thin bar drawn in the middle. */
  .fill {
    position: absolute;
    top: 4px;
    bottom: 4px;
    left: var(--a);
    right: calc(100% - var(--b));
    cursor: grab;
    touch-action: none;
  }

  .fill::before {
    content: "";
    position: absolute;
    inset: 8px 0;
    border-radius: 999px;
    background: var(--series-1);
    transition: inset 0.1s;
  }

  .fill:hover::before,
  .fill:active::before,
  .fill:focus-visible::before {
    inset: 6px 0;
  }

  .fill:active {
    cursor: grabbing;
  }

  .fill:focus-visible {
    outline: 2px solid var(--focus);
    outline-offset: 2px;
    border-radius: 999px;
  }

  .slider input {
    position: absolute;
    inset: 0;
    width: calc(100% + 20px);
    margin: 0 -10px;
    height: 28px;
    background: transparent;
    pointer-events: none;
    appearance: none;
    -webkit-appearance: none;
  }

  .slider input::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: var(--surface);
    border: 2px solid var(--series-1);
    box-shadow: 0 1px 3px rgb(0 0 0 / 0.2);
    pointer-events: auto;
    cursor: grab;
  }

  .slider input::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: var(--surface);
    border: 2px solid var(--series-1);
    box-shadow: 0 1px 3px rgb(0 0 0 / 0.2);
    pointer-events: auto;
    cursor: grab;
  }

  .slider input:focus-visible::-webkit-slider-thumb {
    outline: 2px solid var(--focus);
    outline-offset: 2px;
  }

  .ends {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    font-size: var(--t-xs);
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
</style>
